import React from 'react';
import { ClipboardCheckIcon } from 'lucide-react';
import { loans, paymentTypeOptions } from '../data/payPeriods';
import type { PayPeriod, PaymentFormValues, PaymentType } from '../types/payment';
import { formatCurrency, hasAmount, parseAmount } from '../utils/currency';
import { formatDate, getPrimaryAmount, getVisibility } from '../utils/paymentConfig';
import { SectionCard } from './SectionCard';
interface Row {
  label: string;
  value: string;
  emphasis?: boolean;
}
function buildRows(values: PaymentFormValues, paymentType: PaymentType, payPeriod: PayPeriod): Row[] {
  const v = getVisibility(paymentType, payPeriod);
  const rows: Row[] = [];
  const money = (label: string, raw: string, emphasis = false) => {
    if (hasAmount(raw)) {
      rows.push({
        label,
        value: formatCurrency(parseAmount(raw)),
        emphasis
      });
    }
  };
  if (v.loan) {
    const loan = loans.find(item => item.id === values.loanId);
    rows.push({
      label: 'Loan',
      value: loan ? loan.name : '—'
    });
    if (loan) rows.push({
      label: 'Loan Number',
      value: loan.loanNumber
    });
  }
  if (v.commissionDetails) {
    money('Gross Commission', values.grossCommission);
    if (v.flatFeeField) money('Flat Fee', values.flatFee);
    if (v.processingFeeField) money('Processing Fee', values.processingFee);
    if (v.commissionCorpFee) money('Corp Fee', values.corpFee);
    money('Net Commission', values.netCommission, paymentType === 'payment' || paymentType === 'manager_override');
  }
  if (v.splitDetails) {
    if (values.loSplit) rows.push({
      label: 'LO Split',
      value: `${values.loSplit}%`
    });
    money('LO Amount', values.loAmount);
    money('Payroll Amount', values.payrollAmount);
    money('Balance to P&L', values.balanceToPL);
  }
  if (v.referral) money('Referral Payment', values.referralAmount, true);
  if (v.managerOverride) {
    if (values.managerSplit) rows.push({
      label: 'Manager Split',
      value: `${values.managerSplit}%`
    });
    money('Manager Net Commission', values.managerNetCommission, true);
  }
  if (v.recruitingBonus) money('Recruiting Bonus', values.recruitingBonus, true);
  if (v.expense) {
    money('Expense Amount', values.expenseAmount, true);
    rows.push({
      label: 'Category',
      value: values.expenseCategory || '—'
    });
    rows.push({
      label: 'Expense Date',
      value: formatDate(values.expenseDate)
    });
    rows.push({
      label: 'Detail',
      value: values.expenseDetail || '—'
    });
  }
  if (v.other) {
    money('Payroll Amount', values.payrollAmount, true);
    rows.push({
      label: 'Description',
      value: values.expenseDetail || '—'
    });
  }
  return rows;
}
interface ReviewSummaryProps {
  values: PaymentFormValues;
  paymentType: PaymentType;
  payPeriod: PayPeriod;
}
export function ReviewSummary({
  values,
  paymentType,
  payPeriod
}: ReviewSummaryProps) {
  const rows = buildRows(values, paymentType, payPeriod);
  const typeLabel = paymentTypeOptions.find(option => option.id === paymentType)?.label ?? '';
  const amount = getPrimaryAmount(values, paymentType);
  return <SectionCard title="Review before saving" description="This creates one Payment Line Item on the pay period." icon={ClipboardCheckIcon} data-id="element-191" data-mp="2nypgp2nswjlm">
      <div className="mb-4 flex items-center justify-between rounded-md bg-ink-50 px-4 py-3" data-id="element-192" data-mp="2x0owjrn2xhmz">
        <div data-id="element-193" data-mp="31z041vpwjg21">
          <p className="text-xs font-medium text-ink-500" data-id="element-194" data-mp="31xfdsiamej9z">Payment Type</p>
          <p className="text-sm font-semibold text-ink-900" data-id="element-195" data-mp="24brenpms5mur">{typeLabel}</p>
        </div>
        <div className="text-right" data-id="element-196" data-mp="299t6p026owid">
          <p className="text-xs font-medium text-ink-500" data-id="element-197" data-mp="29aui55n43l0m">Amount</p>
          <p className="numeric text-lg font-semibold text-ink-900" data-id="element-198" data-mp="2e8npi205il2h">
            {formatCurrency(amount)}
          </p>
        </div>
      </div>
      <dl className="divide-y divide-ink-100" data-id="element-199" data-mp="2j172awr0lc68">
        {rows.map(row => <div key={row.label} className="flex items-start justify-between gap-6 py-2.5" data-id="element-200" data-mp="1ljp5ezcgx8uj">
            <dt className="text-xs font-medium text-ink-500" data-id="element-201" data-mp="1qhqlrciieu2b">{row.label}</dt>
            <dd className={`numeric max-w-[60%] text-right text-sm ${row.emphasis ? 'font-semibold text-ink-900' : 'text-ink-800'}`} data-id="element-202" data-mp="3s9k5wajs7zl3">
              {row.value}
            </dd>
          </div>)}
      </dl>
    </SectionCard>;
}